import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import {
  Badge,
  type BadgeVariant,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@beamx/shared';
import { messages, type MessageFilter, type MessageStatus } from '../lib/api/messages.js';

const PAGE_SIZE = 50;

const STATUS_VARIANT: Record<MessageStatus, BadgeVariant> = {
  Sent: 'success',
  Sending: 'default',
  Accepted: 'default',
  Failed: 'destructive',
};

export function ActivityPage() {
  const [filter, setFilter] = useState<MessageFilter>({ take: PAGE_SIZE, skip: 0 });
  const [status, setStatus] = useState<'' | MessageStatus>('');
  const [recipient, setRecipient] = useState('');
  const [templateKey, setTemplateKey] = useState('');

  const onPageOne = (filter.skip ?? 0) === 0;
  const q = useQuery({
    queryKey: ['messages', filter],
    queryFn: () => messages.list(filter),
    placeholderData: keepPreviousData,
    refetchInterval: onPageOne ? 15_000 : false,
  });

  function onApply(e: FormEvent) {
    e.preventDefault();
    setFilter({
      take: PAGE_SIZE,
      skip: 0,
      status: status || undefined,
      toEmail: recipient.trim() || undefined,
      templateKey: templateKey.trim() || undefined,
    });
  }

  function onClear() {
    setStatus('');
    setRecipient('');
    setTemplateKey('');
    setFilter({ take: PAGE_SIZE, skip: 0 });
  }

  const data = q.data;
  const canPrev = (data?.skip ?? 0) > 0;
  const canNext = data ? data.skip + data.take < data.totalCount : false;

  return (
    <>
      <PageHeader title="Activity" description="Every message you’ve sent. Page one auto-refreshes every 15 seconds." />

      <Card className="mb-6">
        <CardContent className="pt-6">
          <form onSubmit={onApply} className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="f-status">Status</Label>
              <Select id="f-status" value={status} onChange={(e) => setStatus(e.target.value as '' | MessageStatus)}>
                <option value="">Any</option>
                <option value="Accepted">Accepted</option>
                <option value="Sending">Sending</option>
                <option value="Sent">Sent</option>
                <option value="Failed">Failed</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-to">Recipient</Label>
              <Input id="f-to" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="alice@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-tpl">Template key</Label>
              <Input id="f-tpl" value={templateKey} onChange={(e) => setTemplateKey(e.target.value)} placeholder="order_confirmation" />
            </div>
            <div className="flex items-end gap-2">
              <Button type="submit" className="flex-1">Apply</Button>
              <Button type="button" variant="ghost" onClick={onClear}>Clear</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : data && data.messages.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Accepted</TableHead>
                    <TableHead className="text-right">Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.messages.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-foreground">{m.toEmail}</TableCell>
                      <TableCell className="max-w-xs truncate" title={m.subject}>{m.subject}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{m.templateKey ?? '—'}</TableCell>
                      <TableCell><Badge variant={STATUS_VARIANT[m.status]} dot>{m.status}</Badge></TableCell>
                      <TableCell className="text-muted-foreground">{new Date(m.acceptedAt).toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm"><Link to={`/messages/${m.id}`}>View</Link></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-muted-foreground">
                <span>{data.totalCount === 0 ? 0 : data.skip + 1}–{Math.min(data.skip + data.take, data.totalCount)} of {data.totalCount}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={!canPrev} onClick={() => setFilter((f) => ({ ...f, skip: Math.max(0, (f.skip ?? 0) - PAGE_SIZE) }))}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={!canNext} onClick={() => setFilter((f) => ({ ...f, skip: (f.skip ?? 0) + PAGE_SIZE }))}>Next</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-6">
              <EmptyState icon={Inbox} title="No messages" description="No messages match these filters yet." />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
